import { Sparkles, Ticket, Medal, Crown } from "lucide-react";
import { ordinal } from "@/lib/timeAgo";
import { pluralize } from "@/lib/format";

// The words people see for rewards and prizes — use these everywhere.
//   School rewards    (milestones, applies_to "campaign") — the school earns
//                     together (unlock), or the top classroom wins
//                     (top_classroom) — in each division or the whole school
//   Classroom rewards (applies_to "classroom")
//   Student prizes    (applies_to "student"), in three styles:
//                     unlock, raffle (drawn across the school or in each
//                     division) and top_in_class ("Top student" — in each
//                     class, each division or the whole school)
export const AWARD_STYLES = {
  unlock: { label: "Everyone who reaches it", short: "Reach a goal", icon: Sparkles },
  raffle: { label: "Prize draw", short: "Prize draw", icon: Ticket },
  top_in_class: { label: "Top student", short: "Top student", icon: Medal },
  top_classroom: { label: "Top classroom", short: "Top classroom", icon: Crown },
};

export const STUDENT_PRIZE_STYLES = ["unlock", "raffle", "top_in_class"];

// $500 or 10,000 minutes — for sentences ("At 10,000 minutes read").
export function formatThreshold(kind, amount) {
  return kind === "money" ? formatPrizeAmount(kind, amount) : `${amount.toLocaleString("en-CA")} minutes`;
}

// One line saying how a reward or prize is earned:
//   "At $500 raised", "At 10,000 minutes read",
//   "1 entry per 100 minutes read", "Top reader in each class",
//   "Top reader in each division"
export function rewardSummary(m) {
  if (m.award_style === "top_classroom") {
    const customMinimum = m.threshold_amount > (m.kind === "money" ? 100 : 1);
    const qualifier = customMinimum ? ` · at least ${topClassroomAmount(m, m.threshold_amount)} to qualify` : "";
    return `${topClassroomMeasure(m)} ${topClassroomWhere(m)}${qualifier}`;
  }
  if (m.award_style === "top_in_class") {
    // The default minimum (1 minute / $1) just means "took part at all".
    const customMinimum = m.threshold_amount > (m.kind === "money" ? 100 : 1);
    const qualifier = customMinimum ? ` · at least ${formatThreshold(m.kind, m.threshold_amount)} to qualify` : "";
    return `${topInClassLabel(m.kind)} ${topStudentWhere(m)}${qualifier}`;
  }
  const measure = m.kind === "money" ? "raised" : "read";
  if (m.award_style === "raffle") {
    return `1 entry per ${formatThreshold(m.kind, m.threshold_amount)} ${measure}${m.competes_within === "division" ? " · drawn in each division" : ""}`;
  }
  return `At ${formatThreshold(m.kind, m.threshold_amount)} ${measure}`;
}

// "Top reader" / "Top fundraiser" — what a top-student prize is for.
export function topInClassLabel(kind) {
  return kind === "money" ? "Top fundraiser" : "Top reader";
}

// "in each class" / "in each division" / "in the school" — who a
// top-student prize's students compete against.
export function topStudentWhere(m) {
  if (m.competes_within === "division") return "in each division";
  if (m.competes_within === "school") return "in the school";
  return "in each class";
}

// "in class" / "in Division 1" / "in the school" — where one student's
// top-student prize is won, from its group_label. Null for the "No
// division" group of a divided school.
export function topStudentGroupWhere(m) {
  if (!m.competes_within || m.competes_within === "classroom") return "in class";
  if (m.competes_within === "school" || m.group_label === "Whole school") return "in the school";
  if (!m.group_label || m.group_label === "No division") return null;
  return `in ${m.group_label}`;
}

// "Top reader in class: Gift card", "Top reader in Division 1: …" — a
// top-student prize as one student sees it. Mirrors
// PrizeStandingHelper#top_in_class_prize_label.
export function topInClassPrizeLabel(m) {
  const where = topStudentGroupWhere(m);
  return `${topInClassLabel(m.kind)}${where ? ` ${where}` : ""}: ${m.prize}`;
}

// Whether a top-classroom prize compares class averages rather than totals.
export function isAverageRanking(rankedBy) {
  return rankedBy === "per_student" || rankedBy === "per_participant";
}

// "Most minutes per student" / "Most raised" — what a top-classroom prize
// ranks classrooms by.
export function topClassroomMeasure(m) {
  const measure = m.kind === "money" ? "Most raised" : "Most minutes read";
  if (m.ranked_by === "per_participant") return `${measure.replace(" read", "")} per participating student`;
  if (m.ranked_by === "per_student") return `${measure.replace(" read", "")} per student`;
  return measure;
}

// "in each division" / "in the school" — who a top-classroom prize's
// classrooms compete against.
export function topClassroomWhere(m) {
  return m.competes_within === "school" ? "in the school" : "in each division";
}

// "45 min per student" / "$300" — an amount on a top-classroom prize.
function topClassroomAmount(m, amount) {
  return `${formatPrizeAmount(m.kind, amount)}${isAverageRanking(m.ranked_by) ? " per student" : ""}`;
}

// "Top classroom in Division 1". Mirrors
// PrizeStandingHelper#top_classroom_prize_label.
export function topClassroomLabel(m) {
  if (!m.division_label || m.division_label === "No division") return "Top classroom";
  if (m.division_label === "Whole school") return "Top classroom in the school";
  return `Top classroom in ${m.division_label}`;
}

// A classroom's own standing for a top-classroom prize — rank and gap
// within its division, never another classroom's name. Mirrors
// PrizeStandingHelper#top_classroom_standing_label (digest email).
export function topClassroomStandingLabel(m) {
  const amount = (n) => topClassroomAmount(m, n);
  const of = `of ${pluralize(m.division_size, "class", "classes")}`;

  if (m.won) return "Won!";
  if (!m.competing) return "Not competing yet";
  if (m.finalized && m.gap_to_qualify > 0) return "Didn't qualify";
  if (m.finalized) return `Finished ${ordinal(m.division_rank)} ${of}`;
  if (m.gap_to_qualify > 0) return `${amount(m.gap_to_qualify)} more to qualify`;
  if (m.tied_for_first) return `Tied for 1st ${of}`;
  if (m.leading) return `1st ${of}`;
  return `${ordinal(m.division_rank)} ${of} · ${amount(m.gap_to_first)} behind 1st`;
}

// Cents shown without ".00" when whole, minutes with a unit. Mirrors
// PrizeStandingHelper#prize_amount.
export function formatPrizeAmount(kind, amount) {
  if (kind !== "money") return `${amount.toLocaleString("en-CA")} min`;

  const digits = amount % 100 === 0 ? 0 : 2;
  return `$${(amount / 100).toLocaleString("en-CA", { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

// A student's own standing for a top-student prize — rank and gap only,
// never anyone else's name. Mirrors
// PrizeStandingHelper#top_in_class_standing_label (digest email).
export function topInClassStandingLabel(m) {
  const amount = (n) => formatPrizeAmount(m.kind, n);
  const of = !m.competes_within || m.competes_within === "classroom" ? "in class" : `of ${pluralize(m.group_size, "student")}`;

  if (m.won) return "Won!";
  if (m.finalized && m.gap_to_qualify > 0) return "Didn't qualify";
  if (m.finalized) return `Finished ${ordinal(m.rank)} ${of}`;
  if (m.gap_to_qualify > 0) return `${amount(m.gap_to_qualify)} more to qualify`;
  if (m.tied_for_first) return `Tied for 1st ${of}`;
  if (m.leading) return `1st ${of}`;
  return `${ordinal(m.rank)} ${of} · ${amount(m.gap_to_first)} behind 1st`;
}

// How a student prize is won, as a sentence:
//   "Each student who reads 500 minutes earns this."
export function prizeDescription(m) {
  const amount = formatThreshold(m.kind, m.threshold_amount);
  const verb = m.kind === "money" ? "raised" : "read";
  if (m.award_style === "raffle") {
    return `Every ${amount} ${verb} earns one entry in the draw.${m.competes_within === "division" ? " A winner is drawn in each division." : ""}`;
  }
  if (m.award_style === "top_in_class") return `Goes to the ${topInClassLabel(m.kind).toLowerCase()} ${topStudentWhere(m)}. Ties all win.`;
  if (m.award_style === "top_classroom") return `Goes to the classroom with the ${topClassroomMeasure(m).toLowerCase()} ${topClassroomWhere(m)}. Ties all win.`;
  return `Each student who ${m.kind === "money" ? "raises" : "reads"} ${amount} earns this.`;
}

// The label on the reward form's amount field.
export function amountFieldLabel({ scope, kind, awardStyle, rankedBy }) {
  const unit = kind === "money" ? "($)" : "(minutes)";
  if (awardStyle === "top_classroom") return `Minimum to qualify${isAverageRanking(rankedBy) ? " per student" : ""} ${unit}`;
  if (awardStyle === "raffle") return kind === "money" ? "One entry for every ($ raised)" : "One entry for every (minutes read)";
  if (awardStyle === "top_in_class") return `Minimum to qualify ${unit}`;
  if (scope === "student") return `Each student earns it at ${unit}`;
  return `Unlocks at ${unit}`;
}
